import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, interval, Subscription, forkJoin } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Globals } from '../global/globals';
import { Notification } from '../dtos/notification';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly notificationUri = this.globals.backendUri + '/notifications';
  private readonly unreadNotificationsSubject = new BehaviorSubject<Notification[]>([]);
  private readonly allNotificationsSubject = new BehaviorSubject<Notification[]>([]);
  private pollingSubscription: Subscription | null = null;

  public readonly unreadNotifications$ = this.unreadNotificationsSubject.asObservable();
  public readonly allNotifications$ = this.allNotificationsSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
    private authService: AuthService,
  ) {}

  /**
   * Start polling for unread notifications. Call this when user logs in.
   * Polls every 20 seconds.
   */
  startPolling(): void {
    if (this.pollingSubscription) {
      return; // Already polling
    }

    // Initial fetch
    this.fetchUnreadNotifications().subscribe();
    this.fetchAllNotifications().subscribe();

    // Poll every 20 seconds — switchMap cancels prior if slow,
    // forkJoin ensures both complete without orphan subscriptions
    this.pollingSubscription = interval(20000)
      .pipe(
        switchMap(() => {
          if (!this.authService.isLoggedIn()) {
            return of([]);
          }
          return forkJoin([this.fetchAllNotifications(), this.fetchUnreadNotifications()]);
        }),
      )
      .subscribe();
  }

  /**
   * Stop polling for notifications. Call this when user logs out.
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    this.unreadNotificationsSubject.next([]);
    this.allNotificationsSubject.next([]);
  }

  /**
   * Fetch unread notifications
   */
  fetchUnreadNotifications(): Observable<Notification[]> {
    return this.httpClient.get<any[]>(this.notificationUri + '/unread').pipe(
      tap((notifications) => {
        const notificationObjects = notifications.map(this.mapToNotification);
        this.unreadNotificationsSubject.next(notificationObjects);
      }),
      catchError((error) => {
        console.error('Error fetching unread notifications:', error);
        return of([]);
      }),
    );
  }

  /**
   * Fetch all notifications (read & unread)
   */
  fetchAllNotifications(): Observable<Notification[]> {
    return this.httpClient.get<any[]>(this.notificationUri).pipe(
      tap((notifications) => {
        const notificationObjects = notifications.map(this.mapToNotification);
        this.allNotificationsSubject.next(notificationObjects);
      }),
      catchError((error) => {
        console.error('Error fetching all notifications:', error);
        return of([]);
      }),
    );
  }

  /**
   * Mark a single notification as read
   */
  markAsRead(id: number): Observable<void> {
    return this.httpClient.put<void>(this.notificationUri + `/${id}/read`, {}).pipe(
      catchError((error) => {
        console.error('Error marking notification as read:', error);
        return of(void 0);
      }),
      tap(() => {
        this.refreshNotifications();
      }),
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<void> {
    return this.httpClient.put<void>(this.notificationUri + '/read-all', {}).pipe(
      catchError((error) => {
        console.error('Error marking all notifications as read:', error);
        return of(void 0);
      }),
      tap(() => {
        this.refreshNotifications();
      }),
    );
  }

  /**
   * Delete a notification
   */
  deleteNotification(id: number): Observable<void> {
    return this.httpClient.delete<void>(this.notificationUri + `/${id}`).pipe(
      catchError((error) => {
        console.error('Error deleting notification:', error);
        return of(void 0);
      }),
      tap(() => {
        this.refreshNotifications();
      }),
    );
  }

  /**
   * Get current unread notifications snapshot
   */
  getUnreadNotifications(): Notification[] {
    return this.unreadNotificationsSubject.value;
  }

  /**
   * Get current unread notification count
   */
  getUnreadCount(): number {
    return this.unreadNotificationsSubject.value.length;
  }

  /**
   * Refresh notifications from the server
   */
  private refreshNotifications(): void {
    this.fetchUnreadNotifications().subscribe();
    this.fetchAllNotifications().subscribe();
  }

  /**
   * Map backend response to Notification object
   */
  private mapToNotification(data: any): Notification {
    return new Notification(
      data.id,
      data.recipientId,
      data.recipientEmail,
      data.type,
      data.title,
      data.message,
      data.relatedGroupId || null,
      data.relatedGroupName || null,
      data.isRead,
      data.createdAt,
      data.actionUrl || null,
    );
  }
}
